import axiosConfig from "./axiosConfig";
import { HealthEvent } from "./getSiteHealthEvents";

export interface HealthOverviewSite {
  siteId: string;
  siteName: string;
  openEvents: HealthEvent[];
  availability: { pct: number | null };
}

/** EPC (excused) figure in pct/ryg; all-cause (excused outages dock too) alongside. */
export interface AvailabilityFigures {
  pct: number | null;
  ryg: string;
  allCausePct?: number | null;
  allCauseRyg?: string;
}

export interface SiteAvailability {
  siteId: string;
  siteName: string;
  lastMonth: AvailabilityFigures;
  last12Months: AvailabilityFigures;
  // Effective contracted threshold (falls back to fleet 99% when the site
  // has no configured `contracted_availability`); flag distinguishes the
  // configured vs default case so the breakdown can mark defaults with `*`.
  contractedPct?: number | null;
  contractedPctIsDefault?: boolean;
  monthsAnalysed?: number;
  latestMonthLabel?: string | null;
}

export interface PRPeriodValue {
  pct: number | null;
  ryg: string | null;
  // Per-period contracted GPR threshold (only populated for the GPR
  // periods, not OPR — OPR uses fixed global thresholds). The default
  // flag is true when the site has no configured gpr_rule or
  // contracted_gpr; the value then comes from the fleet fallback.
  gprPct?: number | null;
  gprPctIsDefault?: boolean;
  designedPrPct?: number | null;
}

export interface SitePR {
  siteId: string;
  siteName: string;
  lastMonth: PRPeriodValue | null;
  lastMonthLabel?: string;
  last12Months: PRPeriodValue | null;
  oprLastMonth?: PRPeriodValue | null;
  oprLast12Months?: PRPeriodValue | null;
  // Deemed variants: curtailed periods credited at modelled potential.
  lastMonthDeemed?: PRPeriodValue | null;
  last12MonthsDeemed?: PRPeriodValue | null;
  oprLastMonthDeemed?: PRPeriodValue | null;
  oprLast12MonthsDeemed?: PRPeriodValue | null;
  monthsAnalysed?: number;
  latestMonthLabel?: string | null;
  curtailmentLossMwh: number | null;
}

export interface RecentHealthEvent extends HealthEvent {
  siteName: string;
}

export interface HealthOverviewResponse {
  sites: HealthOverviewSite[];
  badgeCounts: Record<string, number>;
  aggregateAvailability: AvailabilityFigures;
  allSiteAvailability: SiteAvailability[];
  // BESS availability (24-hour basis), aggregated across battery sites only.
  aggregateBessAvailability?: AvailabilityFigures;
  allSiteBessAvailability?: SiteAvailability[];
  aggregatePR?: {
    lastMonth: PRPeriodValue;
    last12Months: PRPeriodValue;
  };
  aggregateOPR?: {
    lastMonth: PRPeriodValue;
    last12Months: PRPeriodValue;
  };
  // Deemed variants: curtailment credited at modelled potential.
  aggregatePRDeemed?: {
    lastMonth: PRPeriodValue;
    last12Months: PRPeriodValue;
  };
  aggregateOPRDeemed?: {
    lastMonth: PRPeriodValue;
    last12Months: PRPeriodValue;
  };
  allSitePR?: SitePR[];
}

export interface HealthOverviewInitResponse {
  operationKey: string;
  status: string;
}

const postHealthOverview = async (
  authToken: string
): Promise<HealthOverviewInitResponse> => {
  const { data } = await axiosConfig.post(
    "/health/events",
    {},
    {
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );
  return data;
};

export default postHealthOverview;
